import React, { useContext, useEffect, useRef, useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./ContentBox.module.css";
import {
	CustomChipsIcon,
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
	Preview,
} from "../../../../../../../../../../common/components";
import { NavUtils } from "../../../../../../../../../../navigation";
import {
	resetDocument,
	setCurrentReference,
} from "../../../../../../../../slice/notes-selected-document/notes-selected-document-slice";
import {
	resetPdf,
	setHoveredInformation,
	setScrollTo,
	setSelection,
} from "../../../../../../../../slice/notes-pdf/notes-pdf-slice";
import { translate } from "../../../../../../../../../../common/providers";
import { ContentService } from "../../../../../../../../../../api";
import { useApi, useTrackEvent } from "../../../../../../../../../../common/hooks";
import { isNonEmptyArray } from "../../../../../../../../../../common/utils";
import { NotesEditorContext } from "../../../../../../../../notes-editor-context";

const ContentBox = ({ node, updateAttributes, selected, editor, getPos }) => {
	const { call: getPreviewInformationChapter } = useApi(ContentService.getPreviewInformationChapter);
	const { informations, size } = node.attrs;
	const projectId = useSelector(({ context }) => context?.project?.id);
	const documentIdRedux = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.selectedDocument?.id);
	const [preview, setPreview] = useState(null);
	const [inTextSelection, setInTextSelection] = useState(false);
	const clickTimerRef = useRef(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const trackEvent = useTrackEvent();
	const { isDisplayDocViewer, onCloseDocViewer } = useContext(NotesEditorContext);

	useEffect(() => {
		const handleSelectionUpdate = () => {
			const pos = getPos();
			if (typeof pos !== "number") {
				setInTextSelection(false);
				return;
			}
			const { from, to } = editor.state.selection;
			setInTextSelection(from !== to && pos >= from && pos + node.nodeSize <= to);
		};
		editor.on("selectionUpdate", handleSelectionUpdate);
		editor.on("blur", handleSelectionUpdate);
		return () => {
			editor.off("selectionUpdate", handleSelectionUpdate);
			editor.off("blur", handleSelectionUpdate);
		};
	}, [editor, getPos, node.nodeSize]);

	const handleSelectDocument = () => {
		if (informations[0].documentId !== documentIdRedux) {
			dispatch(resetDocument());
			dispatch(resetPdf());
		}
		navigate(NavUtils.goToNotes({ projectId, documentId: informations[0].documentId }));
	};
	const scrollToElement = () => {
		if (documentIdRedux !== informations[0].documentId) {
			handleSelectDocument();
		}
		dispatch(setCurrentReference(null));
		if (informations.length > 1) {
			dispatch(setSelection({ ids: informations.map((i) => ({ informationId: i.itemId })), highlighted: false }));
			dispatch(setHoveredInformation({}));
		} else {
			dispatch(setHoveredInformation({ id: informations[0].itemId, highlighted: false }));
			dispatch(setSelection({}));
		}
		getPreviewInformationChapter({
			docId: informations[0].documentId,
			informationId: informations[0].itemId,
		})
			.then((data) => {
				dispatch(
					setScrollTo({
						page: data.pageStart,
						y1: data.rectangles[0].y1,
						y2: data.rectangles[0].y2,
					})
				);
				dispatch(setCurrentReference(informations[0].itemId));
				if (informations.length > 1) {
					dispatch(
						setSelection({ ids: informations.map((i) => ({ informationId: i.itemId })), highlighted: true })
					);
				} else {
					dispatch(setHoveredInformation({ id: informations[0].itemId, highlighted: true }));
				}
				trackEvent("Notes_Reference_Highlight");
			})
			.catch(console.error);
	};
	const handleUpdateSize = (e) => {
		e.stopPropagation();
		trackEvent(size === "large" ? "Notes_Reference_Fold" : "Notes_Reference_Unfold");
		updateAttributes({ size: size === "large" ? "small" : "large" });
	};
	const handleClosePreview = () => {
		setPreview(null);
	};
	const handleQuoteClick = (e) => {
		e.stopPropagation();
		if (clickTimerRef.current) {
			return;
		}
		clickTimerRef.current = setTimeout(() => {
			clickTimerRef.current = null;
			handleUpdateSize(e);
		}, 250);
	};
	const handleQuoteDoubleClick = (e) => {
		e.stopPropagation();
		if (clickTimerRef.current) {
			clearTimeout(clickTimerRef.current);
			clickTimerRef.current = null;
		}
		if (size === "large") {
			if (isDisplayDocViewer) {
				onCloseDocViewer();
			}
		} else {
			scrollToElement();
		}
		handleUpdateSize(e);
	};
	useEffect(() => () => clearTimeout(clickTimerRef.current), []);
	const isSelected = selected || inTextSelection;
	return (
		<>
			<NodeViewWrapper className={styles.wrapperContainer} data-drag-handle="">
				<CustomTooltip
					title={
						<div>
							<div>
								{informations[0]?.documentName}&nbsp;•&nbsp;p.{informations[0]?.pageStart}
							</div>
							<div>{translate("notes.central-panel.reference.tooltip.click")}</div>
						</div>
					}
				>
					<div
						className={styles.containerSmall}
						data-drag-handle=""
						data-is-opened={size === "large"}
						data-selected={isSelected}
						role="presentation"
						onClick={handleQuoteClick}
						onDoubleClick={handleQuoteDoubleClick}
					>
						<CustomChipsIcon
							className={styles.iconComponent}
							color={(size === "large" && "var(--blue-500)") || "var(--blue-400)"}
							icon={icon.faQuoteLeft}
							size="sm"
						/>
					</div>
				</CustomTooltip>
			</NodeViewWrapper>
			<NodeViewWrapper className={styles.wrapperContainer} data-drag-handle="" data-size-large={size}>
				<span>
					{size === "large" && (
						<div className={styles.containerLarge} data-drag-handle="">
							<div className={styles.additionalInfo}>
								<div className={styles.additionalInfoBox}>
									<IconComponent color="var(--blue-600)" icon={icon.faFile} />
									<span className={styles.documentName} role="presentation" onClick={scrollToElement}>
										{informations[0]?.documentName} • p.{informations[0]?.pageStart}
									</span>
									<CustomIconButton
										icon={icon.faEye}
										size="xs"
										variant="outlined"
										onClick={scrollToElement}
									/>
								</div>
								<CustomIconButton
									color="var(--color-black)"
									icon={icon.faXmark}
									size="xs"
									onClick={handleQuoteClick}
								/>
							</div>
							{isNonEmptyArray(informations) && (
								<div>
									{informations.map((info) => (
										<div key={info.itemId} className={styles.informationContent}>
											<div className={styles.informationContent__textContainer}>
												<div className={styles.informationContent__marker} />
												<div className={styles.informationContent__text}>{info.content}</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}
					<Preview
						coordinates={preview?.rectangles}
						docId={preview?.documentId}
						infoId={preview?.informationId}
						infoType={preview?.informationType}
						open={!!preview}
						page={preview?.pageStart}
						projectId={projectId}
						onClose={handleClosePreview}
					/>
				</span>
			</NodeViewWrapper>
		</>
	);
};

export default ContentBox;
